import { Fetcher, dig } from '@this/src/util';
import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import SuppliedItemMappingTabs from '@this/components/admin/supplied_item_mappings/share/supplied_item_mapping_tabs';
import type { SuppliedItemResponseArgs } from '@this/domain/supplied_item/supplied_item2';
import SuppliedItem, { convertSuppliedItemResponseToArgs } from '@this/domain/supplied_item/supplied_item2';
import type { IndividualOptionSuppliedItemArgs } from '@this/domain/individual_option_supplied_item/individual_option_supplied_item';
import IndividualOptionSuppliedItem from '@this/domain/individual_option_supplied_item/individual_option_supplied_item';
import { Title } from '../admin';
import { AdminContentIn } from '../shared/atom';
import { Button } from '../../shared/ui/inputs/button';

interface Response {
  item_mappings: IndividualOptionSuppliedItemArgs[];
  supplied_items: SuppliedItemResponseArgs[];
}

const IndividualOptionSuppliedItems = () => {
  const styles = useMemo(() => getStyles(), []);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemMappings, setItemMappings] = useState<IndividualOptionSuppliedItem[]>([]);
  const [suppliedItems, setSuppliedItems] = useState<SuppliedItem[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const fetchItemMappings = () => {
    setLoading(true);
    Fetcher.get<Response>('/admin/individual_option_supplied_items.json')
      .then(res => {
        setItemMappings(res.item_mappings.map(mapping => new IndividualOptionSuppliedItem(mapping)));
        setSuppliedItems(
          res.supplied_items.map(item => new SuppliedItem(convertSuppliedItemResponseToArgs(item)))
        );
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const saveMapping = (mapping: IndividualOptionSuppliedItem) => {
    setSaving(true);
    setMessage(null);
    setErrorMessage(null);
    Fetcher.put(`/admin/individual_option_supplied_items/${mapping.id}.json`, mapping.submitParams())
      .then(() => {
        setMessage('保存しました。');
      })
      .catch(e => {
        setErrorMessage(
          dig(e, 'responseJSON', 'message') || '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    fetchItemMappings();
  }, []);

  return (
    <div>
      <Title>仕入商品マッピング</Title>
      <AdminContentIn>
        <div style={styles.description}>個別にデフォルトの仕入商品を設定できます。</div>
        <SuppliedItemMappingTabs />
        {message && (
          <Alert variant="success" onClose={() => setMessage(null)} style={{ marginBottom: '16px' }}>
            {message}
          </Alert>
        )}
        {errorMessage && (
          <Alert variant="danger" onClose={() => setErrorMessage(null)} style={{ marginBottom: '16px' }}>
            {errorMessage}
          </Alert>
        )}
        {loading ? (
          <Loading />
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>利用契約形態</th>
                <th style={styles.th}>仕入商品</th>
                <th style={styles.th}>操作</th>
              </tr>
            </thead>
            <tbody>
              {itemMappings.map(mapping => (
                <tr key={mapping.id}>
                  <td style={styles.td}>{mapping.contractTypeLabel}</td>
                  <td style={styles.td}>
                    <select
                      style={styles.select}
                      value={mapping.suppliedItemId?.toString() || ''}
                      onChange={e => {
                        mapping.suppliedItemId = parseInt(e.target.value, 10) || null;
                      }}
                    >
                      <option value="">選択してください</option>
                      {suppliedItems.map(item => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.supplierName}：{item.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>
                    <Button onClick={() => saveMapping(mapping)} disabled={saving}>
                      保存
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </AdminContentIn>
    </div>
  );
};

const getStyles = () => ({
  description: {
    marginBottom: 40
  },
  table: {
    margin: 0,
    fontSize: 12,
    width: 'auto' as const
  },
  th: {
    padding: 8
  },
  td: {
    padding: 8
  },
  select: {
    margin: 0
  }
});

export default observer(IndividualOptionSuppliedItems);
